/* Smart Budget Matcher — premium banner matching the approved design:
   "Find Jewellery That Matches You", BY PRICE / BY GRAMS tabs, Au / Ag metal
   chips (Platinum removed), gold MATCH JEWELLERY → button. Used end-to-end on
   the homepage and inside the staff dashboards' matcher tool. */
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, Coins, Scale } from "lucide-react";

const METALS = [
  { key: "Gold", symbol: "Au", label: "Gold", chip: "bm-chip-au" },
  { key: "Silver", symbol: "Ag", label: "Silver", chip: "bm-chip-ag" },
];

export default function BudgetMatcherBar({ onMatch, initial = {}, compact = false }) {
  const navigate = useNavigate();
  const [mode, setMode] = useState(initial.grams ? "grams" : "price");
  const [value, setValue] = useState(initial.value || "");
  const [metal, setMetal] = useState(initial.metal || "Gold");

  const go = (e) => {
    e?.preventDefault();
    if (!value || +value <= 0) {
      toast.error(mode === "price" ? "Enter your budget (₹)" : "Enter the weight in grams");
      return;
    }
    const params = { mode, value: +value, metal };
    if (onMatch) { onMatch(params); return; }
    const qp = new URLSearchParams();
    qp.set(mode === "price" ? "budget" : "grams", value);
    qp.set("metal", metal);
    navigate(`/budget-match?${qp}`);
  };

  return (
    <div className={`bm-banner ${compact ? "py-8" : "py-12 lg:py-16"}`} data-testid="budget-matcher-banner">
      <div className="max-w-6xl mx-auto px-6 lg:px-12 text-center">
        {/* Headline */}
        <h2 className={`font-display text-pearl leading-tight ${compact ? "text-2xl lg:text-3xl" : "text-3xl lg:text-5xl"}`}>
          Find Jewellery That Matches You
        </h2>
        <p className="text-pearl/80 text-sm mt-2 mb-6">Tell us your budget or grams — we'll match the jewellery.</p>

        {/* Matcher card */}
        <div className="bm-card max-w-3xl mx-auto text-left">
          {/* Tabs */}
          <div className="flex">
            <button type="button" onClick={() => setMode("price")}
              className={`bm-tab ${mode === "price" ? "on" : ""}`} data-testid="bm-tab-price">
              <Coins size={15} className="text-gold" /> BY PRICE
            </button>
            <button type="button" onClick={() => setMode("grams")}
              className={`bm-tab ${mode === "grams" ? "on" : ""}`} data-testid="bm-tab-grams">
              <Scale size={15} className="text-gold" /> BY GRAMS
            </button>
          </div>

          {/* Body */}
          <form onSubmit={go} className="flex flex-wrap items-end gap-5 p-5 lg:p-6">
            <label className="flex-1 min-w-[170px]">
              <span className="block text-[10px] font-bold tracking-[0.15em] text-pearl/90 uppercase mb-2">
                {mode === "price" ? "Your Budget (₹)" : "Weight (grams)"}
              </span>
              <input
                type="number"
                step={mode === "grams" ? "0.1" : "1"}
                placeholder={mode === "price" ? "Enter amount…" : "Enter grams…"}
                className="bm-input"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                data-testid="bm-value"
              />
            </label>

            <div>
              <span className="block text-[10px] font-bold tracking-[0.15em] text-pearl/90 uppercase mb-2">Metal Type</span>
              <div className="flex gap-4">
                {METALS.map((m) => (
                  <button key={m.key} type="button" onClick={() => setMetal(m.key)}
                    className="flex flex-col items-center gap-1 group" data-testid={`bm-metal-${m.key}`}>
                    <span className={`bm-chip ${m.chip} ${metal === m.key ? "on" : ""}`}>{m.symbol}</span>
                    <span className={`text-[10px] ${metal === m.key ? "text-pearl font-bold" : "text-pearl/60"}`}>{m.label}</span>
                  </button>
                ))}
              </div>
            </div>

            <button type="submit" className="bm-match-btn" data-testid="bm-match">
              MATCH JEWELLERY <ArrowRight size={15} />
            </button>
          </form>
        </div>

        <p className="text-pearl/60 text-[11px] italic mt-4 text-center max-w-3xl mx-auto">
          *Our matching engine calculates real-time market rates for bullion and stones.
        </p>
      </div>
    </div>
  );
}
